import { AsyncThunkOptions, AsyncThunkPayloadCreator } from '@reduxjs/toolkit'
import type { ActionCreatorWithPreparedPayload } from '@reduxjs/toolkit/dist/createAction'
import type {
  AsyncThunkAction,
  AsyncThunkFulfilledActionCreator,
  AsyncThunkPendingActionCreator,
  SerializedError,
} from '@reduxjs/toolkit/dist/createAsyncThunk'
import type { IsAny } from '@reduxjs/toolkit/dist/tsHelpers'
import type { Dispatch } from 'redux'

export type AsyncThunkConfig = {
  state?: unknown
  dispatch?: Dispatch
  extra?: unknown
  rejectValue?: unknown
  serializedErrorType?: unknown
  pendingMeta?: unknown
  fulfilledMeta?: unknown
  rejectedMeta?: unknown
}

type WithStrictNullChecks<True, False> = undefined extends boolean ? False : True

type AsyncThunkActionCreator<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig> = IsAny<
  ThunkArg,
  (arg: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>,
  unknown extends ThunkArg
    ? (arg: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>
    : [ThunkArg] extends [void] | [undefined]
    ? () => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig> // argument contains void
    : [void] extends [ThunkArg] // make optional
    ? (arg?: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig> // argument contains undefined
    : [undefined] extends [ThunkArg]
    ? WithStrictNullChecks<
        // with strict nullChecks: make optional
        (arg?: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>,
        // without strict null checks this will match everything, so don't make it optional
        (arg: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>
      > // default case: normal argument
    : (arg: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>
>

type GetRejectValue<ThunkApiConfig> = ThunkApiConfig extends {
  rejectValue: infer RejectValue
}
  ? RejectValue
  : unknown

type GetRejectedMeta<ThunkApiConfig> = ThunkApiConfig extends {
  rejectedMeta: infer RejectedMeta
}
  ? RejectedMeta
  : unknown

type GetSerializedErrorType<ThunkApiConfig> = ThunkApiConfig extends {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  serializedErrorType: infer GetSerializedErrorType
}
  ? GetSerializedErrorType
  : SerializedError

// eslint-disable-next-line @typescript-eslint/ban-types
export type AsyncThunkRejectedActionCreator<ThunkArg, ThunkApiConfig = {}> = ActionCreatorWithPreparedPayload<
  [Error | null, string, ThunkArg, GetRejectValue<ThunkApiConfig>?, GetRejectedMeta<ThunkApiConfig>?],
  GetRejectValue<ThunkApiConfig>,
  string,
  GetSerializedErrorType<ThunkApiConfig>,
  {
    arg: ThunkArg
    requestId: string
    requestStatus: 'rejected'
    aborted: boolean
    condition: boolean
  } & (
    | ({ rejectedWithValue: false } & {
        [K in keyof GetRejectedMeta<ThunkApiConfig>]?: undefined
      })
    | ({ rejectedWithValue: true } & GetRejectedMeta<ThunkApiConfig>)
  )
>

export type AsyncThunk<Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig> = AsyncThunkActionCreator<
  Returned,
  ThunkArg,
  ThunkApiConfig
> & {
  pending: AsyncThunkPendingActionCreator<ThunkArg, ThunkApiConfig>
  rejected: AsyncThunkRejectedActionCreator<ThunkArg, ThunkApiConfig>
  fulfilled: AsyncThunkFulfilledActionCreator<Returned, ThunkArg, ThunkApiConfig>
  typePrefix: string
}

export interface ToastError {
  message?: string | null
  showToast?: boolean
}

export type RejectError = string | ToastError | undefined | null

export type RejectValue = {
  rejectValue: RejectError | (() => RejectError)
}
export type RejectValuePayload = { rejectValue: string | null }

export interface InitialState<Data = any, Meta = any> {
  loading: boolean
  ready: boolean
  error: string | null
  data: Data | null
  meta?: Meta | null
}

export type IfNotUndefined<Type, True, False> = Type extends undefined ? False : True

export interface DefaultList<Data = any, Meta = any> {
  data: Data
  meta: Meta
}

export type DefaultData<Data = any> = Data extends DefaultList ? Data['data'] : Data
export type DefaultMeta<Data = any> = Data extends DefaultList ? Data['meta'] : never

export type CustomInitialState<
  Data = any,
  AddToInitialState extends Record<string, any> | undefined = undefined,
> = IfNotUndefined<
  AddToInitialState,
  InitialState<DefaultData<Data>, DefaultMeta<Data>> & AddToInitialState,
  InitialState<DefaultData<Data>, DefaultMeta<Data>>
>

export const isList = (data: any): data is DefaultList<typeof data.data, typeof data.meta> =>
  typeof data.meta !== 'undefined'

export interface CreateAsyncSliceReadonlyParams<NameAction extends string = string, NameSlice extends string = string> {
  nameAction: NameAction
  nameSlice: NameSlice
}

export interface CreateAsyncSliceParams<
  Returned,
  ThunkArg = void,
  AddToInitialState extends Record<string, any> | undefined = undefined,
> {
  addToInitialState?: AddToInitialState | (() => AddToInitialState)
  promise: AsyncThunkPayloadCreator<Returned, ThunkArg, RejectValue>
  options?: AsyncThunkOptions<ThunkArg, RejectValue>
  error?: RejectValue['rejectValue']
  isCashing?: boolean
  cash?: number
  optimisticReducer?: (
    currentData: Returned | null,
  ) => Returned extends DefaultList ? DefaultList<Returned['data'], Returned['meta']> : Returned | null
  onPending?: (state: CustomInitialState<Returned, AddToInitialState>) => void
  onRejected?: (state: CustomInitialState<Returned, AddToInitialState>, payload?: string) => void
  onFulfilled?: (state: CustomInitialState<Returned, AddToInitialState>, payload?: Returned) => void
  isAccumulate?: boolean
  accumulateUniqBy?: string
}

export interface CreatorAsyncSliceOptions<RootState> {
  nameReducer: keyof RootState
  isCashing?: boolean
  cash?: number
}
